import { Dialog, Stack, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import React, { forwardRef, useImperativeHandle, useState } from "react";
import { useDispatch } from "react-redux";
import DialogHeader from "../dialog/DialogHeader";
import { appIcons } from "../../assets";
import DialogBody from "../dialog/DialogBody";
import LoginIcon from "@mui/icons-material/Login";
import { useSnackbar } from "../../lib/context/snackbarContext";
import Grid from "@mui/material/Grid2";
import Label from "../label";
import InputField from "../InputField";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import DialogActionButtons from "../dialog/DialogActions";
import { loginValidation } from "../../utils/validation";
import { encryptPassword } from "../../utils";
import { loginUser } from "../../services/authenticationFn";
import { fetchUserAddress } from "../../lib/redux/slices/addressSlice";
import { setUserData } from "../../lib/redux/slices/userSlice";
import AppLoader from "../apploader";
import { useNavigate } from "react-router-dom";
import { t } from "i18next";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing("24px"),
  },
  "& .MuiPaper-root": {
    width: "500px",
    [theme.breakpoints.down("sm")]: {
      margin: 12,
    },
  },
}));

const LoginDialog = forwardRef(({}, ref) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(true);
  const [inputValues, setInputValues] = useState({
    email: "",
    password: "",
  });
  const [inputError, setInputError] = useState({
    email: false,
    isEmailValid: false,
    password: false,
  });

  useImperativeHandle(ref, () => ({
    openDialog(params) {
      setOpen(true);
    },
    closeDialog() {
      handleClose();
    },
  }));

  const handleClose = () => {
    setOpen(false);
    setInputValues({});
    setInputError({});
  };

  const handleChangeInputs = (event) => {
    const { name, value } = event.target;
    setInputError((err) => ({
      ...err,
      [name]: false,
      isEmailValid: name === "email" ? false : inputError.isEmailValid,
    }));
    setInputValues((val) => ({ ...val, [name]: value }));
  };

  const handleLogin = async () => {
    try {
      const validate = loginValidation(inputValues, setInputError);
      if (validate) return;

      const payload = {
        email: inputValues.email,
        password: encryptPassword(inputValues.password),
      };

      setIsLoading(true);
      const response = await loginUser(payload);
      if (response.data.status === "success") {
        const userData = response?.data?.data.user;
        const token = response?.data?.data.token;

        const userWithToken = { ...userData, token };
        localStorage.setItem("user_data", JSON.stringify(userWithToken));
        dispatch(fetchUserAddress(userData._id));
        dispatch(setUserData(userWithToken));
        handleClose();
        showSnackbar(response.data.message, "success");
      } else {
        showSnackbar(response.data.message, "error");
      }
    } catch (error) {
      showSnackbar(error.message, "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <AppLoader isVisible={isLoading} />
      <BootstrapDialog
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        open={open}
        disableScrollLock
      >
        <DialogHeader
          title="loginRequired"
          secondaryHeading="requestLogin"
          icon={<LoginIcon sx={{ fontSize: "2rem", color: "primary.main" }} />}
          onClose={handleClose}
        />
        <DialogBody>
          <Grid container rowSpacing={2}>
            <Grid size={12}>
              <Label text={"emailAddress"} required htmlFor="user-email" />
              <InputField
                id={"user-email"}
                required
                inputError={inputError.email || inputError.isEmailValid}
                helperText={
                  inputError.email
                    ? t("emailAddRequired")
                    : inputError.isEmailValid
                    ? t("invalidEmailFormat")
                    : null
                }
                placeholder={`${t("enter")} ${t("emailAddress")}`}
                type={"email"}
                name={"email"}
                value={inputValues.email}
                onChange={handleChangeInputs}
              />
            </Grid>
            <Grid size={12}>
              <Label text={"password"} required htmlFor="user_log_pass" />
              <InputField
                id={"user_log_pass"}
                placeholder={`${t("enter")} ${t("password")}`}
                type={showPassword ? "password" : "text"}
                InputEndIcon={
                  showPassword ? (
                    <VisibilityOffIcon
                      sx={{
                        cursor: "pointer",
                      }}
                      onClick={() => setShowPassword(false)}
                    />
                  ) : (
                    <VisibilityIcon
                      sx={{
                        cursor: "pointer",
                      }}
                      onClick={() => setShowPassword(true)}
                    />
                  )
                }
                inputError={inputError.password}
                helperText={inputError.password && t("passwordRequired")}
                name={"password"}
                value={inputValues.password}
                onChange={handleChangeInputs}
              />
            </Grid>
          </Grid>
          <Stack alignItems={"flex-end"}>
            <Typography
              my={2}
              sx={{ cursor: "pointer" }}
              onClick={() => navigate("/register")}
              textTransform={"capitalize"}
            >
              {t("createAccount")} ?{" "}
              <Typography component={"span"} color={"primary.main"}>
                {" "}
                {t("signUp")}
              </Typography>
            </Typography>
          </Stack>
        </DialogBody>
        <DialogActionButtons
          onCancel={handleClose}
          confirmText="continue"
          onConfirm={handleLogin}
        />
      </BootstrapDialog>
    </>
  );
});

export default LoginDialog;
